import { Shield, Heart, Building2, ShoppingBag, Truck, Factory, GraduationCap, Zap, Landmark, Store, Home, UtensilsCrossed } from 'lucide-react';
import { Link } from 'react-router-dom';

function Industries() {
  const industries = [
    {
      icon: <Shield className="w-12 h-12" />,
      title: 'Government & Defense',
      description: 'Federal agencies, DoD installations, military bases, and federal facilities requiring rapid deployment and mission-critical reliability.',
      image: 'https://images.unsplash.com/photo-1708733818543-8a895c354151?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Base operations support', 'Emergency response', 'Security infrastructure', 'Logistics coordination']
    },
    {
      icon: <Heart className="w-12 h-12" />,
      title: 'Healthcare',
      description: 'Hospitals, clinics, VA facilities, medical centers, and specialty healthcare facilities with strict compliance requirements and infection control protocols.',
      image: 'https://images.pexels.com/photos/263402/pexels-photo-263402.jpeg?auto=compress&cs=tinysrgb&fit=crop&w=800',
      capabilities: ['Hospital construction', 'Medical office buildings', 'Operating rooms', 'Specialized HVAC systems']
    },
    {
      icon: <Building2 className="w-12 h-12" />,
      title: 'Commercial Real Estate',
      description: 'Office buildings, mixed-use developments, corporate headquarters, and commercial complexes requiring premium quality and on-schedule delivery.',
      image: 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Ground-up construction', 'Tenant improvements', 'Office renovations', 'Building modernization']
    },
    {
      icon: <ShoppingBag className="w-12 h-12" />,
      title: 'Retail',
      description: 'Retail stores, shopping centers, big box retailers, and specialty shops with focus on brand consistency, fast-track schedules, and minimal disruption to operations.',
      image: 'https://images.unsplash.com/photo-1441986300917-64674bd600d8?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Store rollouts', 'Retail buildouts', 'Shopping centers', 'Branded environments']
    },
    {
      icon: <UtensilsCrossed className="w-12 h-12" />,
      title: 'Restaurant & Hospitality',
      description: 'Restaurants, hotels, entertainment venues, and hospitality facilities requiring specialized kitchen systems, guest experience focus, and rapid turnaround.',
      image: 'https://images.unsplash.com/photo-1517248135467-4c7edcad34c4?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Restaurant buildouts', 'Hotel construction', 'Kitchen systems', 'Entertainment venues']
    },
    {
      icon: <Truck className="w-12 h-12" />,
      title: 'Infrastructure',
      description: 'Transportation systems, utilities, public works, and critical infrastructure requiring engineering expertise and large-scale project management.',
      image: 'https://images.unsplash.com/photo-1757030689705-1eecc41d221a?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Civil engineering', 'Utilities installation', 'Site development', 'Transportation facilities']
    },
    {
      icon: <Factory className="w-12 h-12" />,
      title: 'Industrial & Manufacturing',
      description: 'Warehouses, distribution centers, production facilities, and industrial complexes built for operational efficiency and heavy-duty requirements.',
      image: 'https://images.unsplash.com/photo-1565793298595-6a879b1d9492?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Warehouse construction', 'Distribution centers', 'Manufacturing facilities', 'Industrial systems']
    },
    {
      icon: <GraduationCap className="w-12 h-12" />,
      title: 'Education',
      description: 'Schools, universities, training facilities, and educational institutions requiring safe, durable construction with minimal disruption to operations.',
      image: 'https://images.unsplash.com/photo-1562774053-701939374585?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['School construction', 'Campus facilities', 'Training centers', 'Educational renovations']
    },
    {
      icon: <Zap className="w-12 h-12" />,
      title: 'Energy',
      description: 'Power generation facilities, temporary power solutions, energy infrastructure, and utility projects supporting critical operations nationwide.',
      image: 'https://images.unsplash.com/photo-1473341304170-971dccb5ac1e?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Temporary power systems', 'Energy facilities', 'Utility infrastructure', 'Emergency power']
    },
    {
      icon: <Home className="w-12 h-12" />,
      title: 'Multi-Family Residential',
      description: 'Apartments, condominiums, townhomes, and mixed-use residential developments with focus on quality construction, efficiency, and resident satisfaction.',
      image: 'https://images.unsplash.com/photo-1545324418-cc1a3fa10c00?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Apartment construction', 'Condo developments', 'Townhome communities', 'Mixed-use residential']
    },
    {
      icon: <Landmark className="w-12 h-12" />,
      title: 'Financial Institutions',
      description: 'Banks, credit unions, financial centers, and secure facilities requiring high-end finishes, security systems, and specialized infrastructure.',
      image: 'https://images.unsplash.com/photo-1541354329998-f4d9a9f9297f?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Bank branch construction', 'Vault installation', 'Secure facilities', 'Financial office buildings']
    },
    {
      icon: <Store className="w-12 h-12" />,
      title: 'Convenience Stores',
      description: 'C-store construction and remodels, fuel station facilities, and multi-location rollouts with rapid turnaround and operational excellence.',
      image: 'https://images.unsplash.com/photo-1604719312566-8912e9227c6a?fm=webp&q=70&auto=format&fit=max&w=800',
      capabilities: ['Store construction', 'Multi-site rollouts', 'Fuel system installation', 'Quick-turn remodels']
    }
  ];

  return (
    <div className="pt-28">
      {/* Hero Section */}
      <section className="bg-black py-24 relative overflow-hidden">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 shimmer-wave"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <span className="text-gold text-sm font-semibold tracking-[0.3em] uppercase mb-4 block">Our Expertise</span>
          <div className="w-24 h-1 bg-gold mx-auto mb-8"></div>
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-6 leading-tight font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Industries <span className="text-gold">We Serve</span>
          </h1>
          <p className="text-gray-300 text-xl max-w-4xl mx-auto">
            Deep expertise across critical sectors—from government and defense to healthcare, commercial, and beyond. We bring specialized knowledge and proven performance to every industry we serve.
          </p>
        </div>
      </section>

      {/* Industries Grid */}
      <section className="bg-black py-32 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="absolute inset-0 brass-particles"></div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-12">
            {industries.map((industry, index) => {
              // Map industry titles to their routes
              const routeMap = {
                'Government & Defense': '/industries/government-defense',
                'Healthcare': '/industries/healthcare',
                'Commercial Real Estate': '/industries/commercial-real-estate',
                'Retail': '/industries/retail',
                'Restaurant & Hospitality': '/industries/restaurant-hospitality',
                'Infrastructure': '/industries/infrastructure',
                'Industrial & Manufacturing': '/industries/industrial-manufacturing',
                'Education': '/industries/education',
                'Energy': '/industries/energy',
                'Financial Institutions': '/industries/financial-institutions',
                'Convenience Stores': '/industries/convenience-stores',
                'Multi-Family Residential': '/industries/multi-family'
              };
              
              return (
                <Link
                  key={index}
                  to={routeMap[industry.title]}
                  className="block relative overflow-hidden rounded-lg border border-gold/20 hover:border-gold transition-all group cursor-pointer"
                >
                  {/* Background Image */}
                  <div 
                    className="absolute inset-0 bg-cover bg-center transition-transform duration-500 group-hover:scale-105"
                    style={{ backgroundImage: `url(${industry.image})` }}
                  ></div>
                  
                  {/* Dark Overlay */}
                  <div className="absolute inset-0 bg-gradient-to-r from-black/80 via-black/50 to-transparent"></div>
                  
                  {/* Content */}
                  <div className="relative z-10 p-10">
                    <div className="grid md:grid-cols-12 gap-8 items-center">
                      <div className="md:col-span-8">
                        <div className="flex items-center space-x-4 mb-6">
                          <div className="text-gold group-hover:scale-110 transition-transform">
                            {industry.icon}
                          </div>
                          <h3 className="text-3xl font-bold text-white font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
                            {industry.title}
                          </h3>
                        </div>
                        <p className="text-gray-300 text-lg leading-relaxed mb-6">
                          {industry.description}
                        </p>
                        <div className="flex flex-wrap gap-3">
                          {industry.capabilities.map((capability, idx) => (
                            <span 
                              key={idx}
                              className="bg-gold/10 border border-gold/30 text-gold px-4 py-2 rounded-full text-sm font-semibold"
                            >
                              {capability}
                            </span>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-black py-24 relative overflow-hidden border-t border-gold/30">
        <div className="absolute inset-0 brass-lighting"></div>
        <div className="absolute inset-0 geometric-brass"></div>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <h3 className="text-4xl md:text-5xl font-bold text-white mb-6 font-serif" style={{ fontFamily: 'Playfair Display, Georgia, serif' }}>
            Your Industry. <span className="text-gold">Our Expertise.</span>
          </h3>
          <p className="text-gray-300 text-xl mb-10">
            Partner with a contractor who understands your sector's unique requirements
          </p>
          <Link 
            to="/contact"
            className="inline-block bg-gold hover:bg-gold-dark text-black font-bold py-5 px-12 rounded text-lg transition-all transform hover:scale-105 uppercase tracking-wider"
          >
            Discuss Your Project
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Industries;
